import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CardRenderer from "@/components/cartes/CardRenderer";
import { mediaUrl, qrUrl, publicUrl, vcardUrl } from "@/lib/cartes";
import {
  ArrowLeft, Upload, Plus, Trash2, Sparkles, Check, Loader2, Globe, Eye, EyeOff,
  Type, Palette, LayoutGrid, Share2, ImagePlus, X,
  QrCode, Smartphone, MessageCircle, Mail, Link2, UserPlus, HelpCircle, Apple,
} from "lucide-react";

const TABS = [["content", "Contenu", Type], ["design", "Design", Palette], ["sections", "Sections", LayoutGrid], ["share", "Partage & SEO", Share2]];
const ACTION_LABELS = { call: "Appeler", sms: "SMS", whatsapp: "WhatsApp", email: "E-mail", website: "Site web", directions: "Itinéraire", devis: "Demander un devis" };
const SOCIAL_OPTS = ["facebook", "instagram", "linkedin", "twitter", "youtube", "tiktok", "whatsapp", "website", "other"];

export default function EditeurCarte() {
  const { id } = useParams();
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [card, setCard] = useState(null);
  const [tab, setTab] = useState("content");
  const [saveState, setSaveState] = useState("saved"); // saved | saving | dirty
  const [aiOpen, setAiOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    Promise.all([api.get("/cartes/meta"), api.get(`/cartes/${id}`)])
      .then(([m, c]) => { setMeta(m.data); setCard(c.data); })
      .catch(() => { toast.error("Carte introuvable."); nav("/ma-carte"); });
  }, [id, nav]);

  const scheduleSave = useCallback((next) => {
    setSaveState("dirty");
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSaveState("saving");
      try {
        const body = (({ card_type, template_id, theme, identity, about, contact, hours, socials, services, gallery, team, actions, links, event, seo, visibility, lang }) =>
          ({ card_type, template_id, theme, identity, about, contact, hours, socials, services, gallery, team, actions, links, event, seo, visibility, lang }))(next);
        await api.put(`/cartes/${id}`, body);
        setSaveState("saved");
      } catch { setSaveState("dirty"); toast.error("Échec de l'enregistrement."); }
    }, 850);
  }, [id]);

  // met à jour une portion de la carte + planifie la sauvegarde
  const patch = useCallback((partial) => {
    setCard((prev) => { const next = { ...prev, ...partial }; scheduleSave(next); return next; });
  }, [scheduleSave]);
  const patchObj = (field, partial) => patch({ [field]: { ...(card[field] || {}), ...partial } });

  const upload = async (file, onUrl) => {
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/cartes/${id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }); onUrl(data.url); toast.success("Image ajoutée."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'upload."); }
  };

  const publish = async () => {
    try { const { data } = await api.post(`/cartes/${id}/publish`); patch({ status: "published", visibility: data.visibility }); setCard((p) => ({ ...p, status: "published", visibility: data.visibility })); toast.success("Carte publiée !"); setGuideOpen(true); }
    catch { toast.error("Échec de la publication."); }
  };
  const unpublish = async () => {
    try { await api.post(`/cartes/${id}/unpublish`); setCard((p) => ({ ...p, status: "draft" })); toast.success("Carte dépubliée."); }
    catch { toast.error("Échec."); }
  };
  const saveSlug = async (newSlug) => {
    try { const { data } = await api.put(`/cartes/${id}/slug`, { slug: newSlug }); setCard((p) => ({ ...p, slug: data.slug })); toast.success("Adresse mise à jour !"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Adresse indisponible."); }
  };

  if (!card || !meta) return <div className="text-slate-400 py-16 text-center"><Loader2 className="animate-spin mx-auto" /></div>;

  const idn = card.identity || {};
  const c = card.contact || {};
  const colors = card.theme?.colors || {};

  return (
    <div data-testid="editeur-carte">
      {/* Barre haute */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <Button variant="ghost" size="sm" onClick={() => nav("/ma-carte")} className="text-slate-500"><ArrowLeft size={16} className="mr-1" /> Mes cartes</Button>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400" data-testid="save-state">
            {saveState === "saving" ? <><Loader2 size={12} className="inline animate-spin mr-1" />Enregistrement…</> :
             saveState === "dirty" ? "Modifications non enregistrées" : <><Check size={12} className="inline mr-1 text-emerald-500" />Enregistré</>}
          </span>
          {card.status === "published"
            ? <Button size="sm" variant="outline" onClick={unpublish} data-testid="unpublish-btn"><EyeOff size={14} className="mr-1" /> Dépublier</Button>
            : <Button size="sm" onClick={publish} className="bg-[#E05638] hover:bg-[#c94a2f]" data-testid="publish-btn"><Globe size={14} className="mr-1" /> Publier</Button>}
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-5">
        {/* FORMULAIRE */}
        <div className="lg:col-span-3 min-w-0">
          <div className="flex gap-1 mb-4 bg-white border border-slate-200 rounded-xl p-1 overflow-x-auto">
            {TABS.map(([k, label, Icon]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold whitespace-nowrap ${tab === k ? "bg-[#E05638] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                <Icon size={15} /> {label}
              </button>
            ))}
          </div>

          {/* ---- CONTENU ---- */}
          {tab === "content" && (
            <div className="space-y-4">
              <Button variant="outline" onClick={() => setAiOpen(true)} className="w-full border-dashed border-[#E05638] text-[#E05638]" data-testid="ai-assist-btn"><Sparkles size={15} className="mr-1" /> Assistant IA — rédiger / suggérer</Button>

              <Panel title="Identité">
                <div className="flex gap-3 mb-3">
                  <ImageField label="Logo / Photo" url={idn.logo_url || idn.photo_url} round onUpload={(f) => upload(f, (url) => patchObj("identity", { logo_url: url, photo_url: url }))} onClear={() => patchObj("identity", { logo_url: "", photo_url: "" })} testid="up-logo" />
                  <ImageField label="Couverture" url={idn.cover_url} onUpload={(f) => upload(f, (url) => patchObj("identity", { cover_url: url }))} onClear={() => patchObj("identity", { cover_url: "" })} testid="up-cover" />
                </div>
                <Field label="Nom affiché"><Input value={idn.display_name || ""} onChange={(e) => patchObj("identity", { display_name: e.target.value })} data-testid="f-name" /></Field>
                <Field label={card.card_type === "evenement" ? "Organisateur" : "Entreprise / Organisation"}><Input value={idn.org_name || ""} onChange={(e) => patchObj("identity", { org_name: e.target.value })} data-testid="f-org" /></Field>
                <Field label="Fonction / Métier"><Input value={idn.role_title || ""} onChange={(e) => patchObj("identity", { role_title: e.target.value })} data-testid="f-role" /></Field>
                <Field label="Slogan"><Input value={idn.slogan || ""} onChange={(e) => patchObj("identity", { slogan: e.target.value })} data-testid="f-slogan" /></Field>
              </Panel>

              <Panel title="Présentation">
                <Textarea rows={4} value={card.about || ""} onChange={(e) => patch({ about: e.target.value })} placeholder="Présentez-vous en quelques phrases…" data-testid="f-about" />
              </Panel>

              {card.card_type === "evenement" && (
                <Panel title="Événement">
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Date"><Input value={card.event?.date || ""} onChange={(e) => patchObj("event", { date: e.target.value })} placeholder="Sam. 12 juillet" data-testid="f-event-date" /></Field>
                    <Field label="Heure"><Input value={card.event?.time || ""} onChange={(e) => patchObj("event", { time: e.target.value })} placeholder="20h00" /></Field>
                  </div>
                  <Field label="Lieu"><Input value={card.event?.place || ""} onChange={(e) => patchObj("event", { place: e.target.value })} data-testid="f-event-place" /></Field>
                  <Field label="Programme"><Textarea rows={3} value={card.event?.program || ""} onChange={(e) => patchObj("event", { program: e.target.value })} /></Field>
                </Panel>
              )}

              <Panel title="Coordonnées">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Téléphone"><Input value={c.phone || ""} onChange={(e) => patchObj("contact", { phone: e.target.value })} data-testid="f-phone" /></Field>
                  <Field label="WhatsApp"><Input value={c.whatsapp || ""} onChange={(e) => patchObj("contact", { whatsapp: e.target.value })} /></Field>
                  <Field label="E-mail"><Input value={c.email || ""} onChange={(e) => patchObj("contact", { email: e.target.value })} data-testid="f-email" /></Field>
                  <Field label="Site web"><Input value={c.website || ""} onChange={(e) => patchObj("contact", { website: e.target.value })} /></Field>
                </div>
                <Field label="Adresse"><Input value={c.address || ""} onChange={(e) => patchObj("contact", { address: e.target.value })} data-testid="f-address" /></Field>
                <label className="flex items-center gap-2 text-sm text-slate-600 mt-1"><Switch checked={!!c.show_address} onCheckedChange={(v) => patchObj("contact", { show_address: v })} data-testid="f-show-address" /> Afficher l'adresse publiquement</label>
                <Field label="Horaires"><Textarea rows={2} value={card.hours || ""} onChange={(e) => patch({ hours: e.target.value })} placeholder="Lun–Ven 9h–18h…" /></Field>
              </Panel>

              <ListPanel title="Services / Prestations" items={card.services || []} cols={[["title", "Titre"], ["desc", "Description"], ["price", "Prix (optionnel)"]]} onChange={(v) => patch({ services: v })} testid="services" />
              <ListPanel title="Équipe / Membres" items={card.team || []} cols={[["name", "Nom"], ["role", "Rôle"]]} onChange={(v) => patch({ team: v })} testid="team" />
              <ListPanel title="Liens personnalisés" items={card.links || []} cols={[["label", "Libellé"], ["url", "URL"]]} onChange={(v) => patch({ links: v })} testid="links" />

              <Panel title="Réseaux sociaux">
                {(card.socials || []).map((s, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <Select value={s.type || "facebook"} onValueChange={(v) => { const n = [...card.socials]; n[i] = { ...n[i], type: v }; patch({ socials: n }); }}>
                      <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>{SOCIAL_OPTS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input value={s.url || ""} onChange={(e) => { const n = [...card.socials]; n[i] = { ...n[i], url: e.target.value }; patch({ socials: n }); }} placeholder="https://…" />
                    <Button variant="ghost" size="sm" onClick={() => patch({ socials: card.socials.filter((_, j) => j !== i) })}><Trash2 size={14} /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => patch({ socials: [...(card.socials || []), { type: "facebook", url: "" }] })} data-testid="add-social"><Plus size={14} className="mr-1" /> Ajouter un réseau</Button>
              </Panel>
            </div>
          )}

          {/* ---- DESIGN ---- */}
          {tab === "design" && (
            <div className="space-y-4">
              <Panel title="Palette de couleurs">
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                  {Object.entries(meta.palettes).map(([key, p]) => (
                    <button key={key} onClick={() => patchObj("theme", { colors: p, palette_key: key })} data-testid={`palette-${key}`}
                      className={`h-14 rounded-xl border-2 overflow-hidden flex ${card.theme?.palette_key === key ? "border-[#E05638] ring-2 ring-[#E05638]/30" : "border-slate-200"}`} title={key}>
                      <span className="flex-1" style={{ background: p.bg }} /><span className="flex-1" style={{ background: p.primary }} /><span className="flex-1" style={{ background: p.secondary }} />
                    </button>
                  ))}
                </div>
              </Panel>
              <Panel title="Typographie">
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(meta.fonts).map(([key, f]) => (
                    <button key={key} onClick={() => patchObj("theme", { fonts: f, font_key: key })} data-testid={`font-${key}`}
                      className={`p-3 rounded-xl border text-left ${card.theme?.font_key === key ? "border-[#E05638] bg-orange-50" : "border-slate-200"}`}>
                      <div style={{ fontFamily: f.title }} className="font-bold text-slate-900">Titre {key}</div>
                      <div style={{ fontFamily: f.body }} className="text-xs text-slate-500">Texte courant</div>
                    </button>
                  ))}
                </div>
              </Panel>
              <Panel title="Couleurs fines">
                <div className="grid grid-cols-2 gap-3">
                  {[["primary", "Accent"], ["button", "Boutons"], ["bg", "Fond"], ["title", "Titres"], ["text", "Texte"], ["surface", "Cartes"]].map(([k, label]) => (
                    <label key={k} className="flex items-center justify-between text-sm text-slate-600 border border-slate-200 rounded-lg px-3 py-1.5">
                      {label}
                      <input type="color" value={colors[k] || "#000000"} onChange={(e) => patchObj("theme", { colors: { ...colors, [k]: e.target.value } })} className="w-8 h-8 rounded cursor-pointer" data-testid={`color-${k}`} />
                    </label>
                  ))}
                </div>
              </Panel>
            </div>
          )}

          {/* ---- SECTIONS ---- */}
          {tab === "sections" && (
            <div className="space-y-4">
              <Panel title="Boutons d'action">
                <p className="text-xs text-slate-500 mb-2">Activez les boutons affichés sur votre carte. Ils utilisent vos coordonnées.</p>
                {Object.entries(ACTION_LABELS).map(([k, label]) => (
                  <label key={k} className="flex items-center justify-between py-1.5 text-sm text-slate-700">
                    {label}
                    <Switch checked={!!(card.actions || {})[k]} onCheckedChange={(v) => patchObj("actions", { [k]: v })} data-testid={`action-${k}`} />
                  </label>
                ))}
              </Panel>
              <Panel title="Galerie photos">
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {(card.gallery || []).map((g, i) => (
                    <div key={i} className="relative group">
                      <img src={mediaUrl(g)} alt="" className="w-full aspect-square object-cover rounded-lg" />
                      <button onClick={() => patch({ gallery: card.gallery.filter((_, j) => j !== i) })} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5"><X size={12} /></button>
                    </div>
                  ))}
                  <label className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-[#E05638]" data-testid="add-gallery">
                    <ImagePlus size={20} className="text-slate-400" />
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], (url) => patch({ gallery: [...(card.gallery || []), url] }))} />
                  </label>
                </div>
              </Panel>
            </div>
          )}

          {/* ---- PARTAGE & SEO ---- */}
          {tab === "share" && (
            <div className="space-y-4">
              <Panel title="Adresse personnalisée">
                <p className="text-xs text-slate-500 mb-2">Choisissez le lien de votre carte — idéal pour le partage et le référencement.</p>
                <SlugField current={card.slug} onSave={saveSlug} />
              </Panel>
              <Panel title="Visibilité">
                <Select value={card.visibility || "private"} onValueChange={(v) => patch({ visibility: v })}>
                  <SelectTrigger data-testid="visibility-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Publique (visible et indexable sur Google)</SelectItem>
                    <SelectItem value="unlisted">Non répertoriée (accessible par lien uniquement)</SelectItem>
                    <SelectItem value="private">Privée (vous seul)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-2">La publication rend la carte accessible. Une carte privée n'est jamais consultable publiquement.</p>
              </Panel>
              <Panel title="Référencement (SEO)">
                <Field label="Titre SEO"><Input value={card.seo?.title || ""} onChange={(e) => patchObj("seo", { title: e.target.value })} placeholder="Titre affiché sur Google" data-testid="seo-title" /></Field>
                <Field label="Meta description"><Textarea rows={2} value={card.seo?.description || ""} onChange={(e) => patchObj("seo", { description: e.target.value })} /></Field>
                {Array.isArray(card.seo?.keywords) && card.seo.keywords.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2" data-testid="seo-keywords">
                    {card.seo.keywords.map((k, i) => <span key={i} className="text-[11px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{k}</span>)}
                  </div>
                )}
                <SeoAssistant cardId={id} onApply={(s) => patchObj("seo", { title: s.title, description: s.description, keywords: s.keywords })} />
              </Panel>
              {card.status === "published" && (
                <Panel title="Lien & QR code">
                  <div className="flex items-center gap-2 mb-3">
                    <Input readOnly value={publicUrl(card.slug)} className="text-xs" data-testid="public-link" />
                    <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(publicUrl(card.slug)); toast.success("Lien copié !"); }}>Copier</Button>
                  </div>
                  <img src={qrUrl(card.slug)} alt="QR code" className="w-40 h-40 border border-slate-100 rounded-xl" data-testid="editor-qr" />
                  <Button variant="outline" onClick={() => setGuideOpen(true)} className="w-full mt-3 border-dashed border-[#E05638] text-[#E05638]" data-testid="share-guide-btn">
                    <HelpCircle size={15} className="mr-1.5" /> Comment utiliser et partager ma carte ?
                  </Button>
                </Panel>
              )}
            </div>
          )}
        </div>

        {/* APERÇU */}
        <div className="lg:col-span-2">
          <div className="lg:sticky lg:top-6">
            <p className="text-xs text-slate-400 mb-2 text-center">Aperçu en direct</p>
            <div className="mx-auto w-[300px] h-[600px] rounded-[2.2rem] border-[10px] border-slate-900 overflow-hidden shadow-2xl bg-white" data-testid="live-preview">
              <div className="w-full h-full overflow-y-auto"><CardRenderer card={card} /></div>
            </div>
          </div>
        </div>
      </div>

      {aiOpen && <AiModal card={card} meta={meta} onClose={() => setAiOpen(false)} onApply={(s) => {
        const upd = {};
        if (s.about) upd.about = s.about;
        if (s.slogan) upd.identity = { ...card.identity, slogan: s.slogan };
        if (Array.isArray(s.services) && s.services.length) upd.services = s.services.map((x) => ({ title: x.title || "", desc: x.desc || "", price: "" }));
        if (s.palette_key && meta.palettes[s.palette_key]) upd.theme = { ...card.theme, colors: meta.palettes[s.palette_key], palette_key: s.palette_key };
        if (s.template_id) upd.template_id = s.template_id;
        patch(upd); setAiOpen(false); toast.success("Suggestions appliquées !");
      }} />}
      {guideOpen && <ShareGuideModal card={card} onClose={() => setGuideOpen(false)} />}
    </div>
  );
}

/* --------- sous-composants --------- */
function Panel({ title, children }) {
  return <div className="bg-white border border-slate-200 rounded-2xl p-4"><h3 className="font-bold text-slate-800 text-sm mb-3">{title}</h3>{children}</div>;
}
function Field({ label, children }) {
  return <div className="mb-2"><label className="text-xs font-semibold text-slate-500 block mb-1">{label}</label>{children}</div>;
}
function SlugField({ current, onSave }) {
  const [val, setVal] = useState(current || "");
  useEffect(() => { setVal(current || ""); }, [current]);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return (
    <div>
      <div className="flex items-center gap-1 flex-wrap">
        <span className="text-xs text-slate-400">{origin}/carte/</span>
        <Input value={val} onChange={(e) => setVal(e.target.value)} className="flex-1 min-w-[140px]" data-testid="slug-input" />
        <Button size="sm" disabled={!val.trim() || val === current} onClick={() => onSave(val.trim())} className="bg-[#E05638] hover:bg-[#c94a2f]" data-testid="slug-save">Enregistrer</Button>
      </div>
    </div>
  );
}
function ImageField({ label, url, round, onUpload, onClear, testid }) {
  return (
    <div className="text-center">
      <label className={`block ${round ? "w-20 h-20 rounded-full" : "w-28 h-20 rounded-xl"} border-2 border-dashed border-slate-300 overflow-hidden cursor-pointer hover:border-[#E05638] relative`} data-testid={testid}>
        {url ? <img src={mediaUrl(url)} alt="" className="w-full h-full object-cover" /> : <span className="flex items-center justify-center w-full h-full text-slate-400"><Upload size={18} /></span>}
        <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} />
      </label>
      <div className="text-[10px] text-slate-400 mt-1">{label}{url && <button onClick={onClear} className="ml-1 text-rose-500">✕</button>}</div>
    </div>
  );
}
function ListPanel({ title, items, cols, onChange, testid }) {
  const add = () => onChange([...(items || []), {}]);
  const set = (i, k, v) => { const n = [...items]; n[i] = { ...n[i], [k]: v }; onChange(n); };
  const del = (i) => onChange(items.filter((_, j) => j !== i));
  return (
    <Panel title={title}>
      {(items || []).map((it, i) => (
        <div key={i} className="flex gap-2 mb-2 items-start" data-testid={`${testid}-row-${i}`}>
          <div className="flex-1 space-y-1">
            {cols.map(([k, ph]) => <Input key={k} value={it[k] || ""} onChange={(e) => set(i, k, e.target.value)} placeholder={ph} />)}
          </div>
          <Button variant="ghost" size="sm" onClick={() => del(i)}><Trash2 size={14} /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add} data-testid={`add-${testid}`}><Plus size={14} className="mr-1" /> Ajouter</Button>
    </Panel>
  );
}
function AiModal({ card, meta, onClose, onApply }) {  const [activity, setActivity] = useState(card.identity?.role_title || "");
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [sugg, setSugg] = useState(null);
  const run = async () => {
    setLoading(true); setSugg(null);
    try {
      const { data } = await api.post("/cartes/ai/suggest", {
        card_type: card.card_type, activity, prompt,
        name: card.identity?.display_name || card.identity?.org_name || "",
      });
      setSugg(data.suggestion);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'IA."); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="ai-modal">
      <div className="bg-white rounded-2xl p-5 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-bold text-lg flex items-center gap-2 mb-3"><Sparkles className="text-[#E05638]" size={20} /> Assistant IA</h3>
        <Field label="Votre activité / secteur"><Input value={activity} onChange={(e) => setActivity(e.target.value)} placeholder="Ex. plombier chauffagiste" data-testid="ai-activity" /></Field>
        <Field label="Précisions (optionnel)"><Textarea rows={2} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Ex. ton chaleureux, 10 ans d'expérience, intervention rapide…" /></Field>
        <Button onClick={run} disabled={loading} className="w-full bg-[#E05638] hover:bg-[#c94a2f]" data-testid="ai-run">{loading ? <><Loader2 size={15} className="animate-spin mr-1" /> Génération…</> : "Générer des propositions"}</Button>
        {sugg && (
          <div className="mt-4 space-y-2 text-sm border-t pt-4" data-testid="ai-result">
            {sugg.slogan && <div><span className="font-semibold">Slogan :</span> {sugg.slogan}</div>}
            {sugg.about && <div><span className="font-semibold">Présentation :</span> <p className="text-slate-600">{sugg.about}</p></div>}
            {Array.isArray(sugg.services) && sugg.services.length > 0 && <div><span className="font-semibold">Services :</span> {sugg.services.map((s) => s.title).join(", ")}</div>}
            {sugg.palette_key && <div><span className="font-semibold">Palette suggérée :</span> {sugg.palette_key}</div>}
            <div className="flex gap-2 pt-2">
              <Button onClick={() => onApply(sugg)} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="ai-apply"><Check size={15} className="mr-1" /> Appliquer</Button>
              <Button variant="outline" onClick={onClose}>Fermer</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ShareGuideModal({ card, onClose }) {
  const slug = card.slug;
  const directUrl = publicUrl(slug);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  // Lien avec aperçu social soigné (Facebook/WhatsApp lisent les balises OG)
  const shareUrl = `${BACKEND}/api/cartes/og/${slug}`;
  const name = card.identity?.display_name || card.identity?.org_name || "ma carte";
  const text = `Découvrez ${name} sur PROXIreduc-28`;
  const [showQr, setShowQr] = useState(false);

  const openExt = (href) => window.open(href, "_blank", "noopener,noreferrer");
  const shareNative = async () => {
    if (navigator.share) { try { await navigator.share({ title: name, text, url: shareUrl }); } catch { /* annulé */ } }
    else { navigator.clipboard.writeText(shareUrl); toast.success("Lien copié !"); }
  };
  const copy = () => { navigator.clipboard.writeText(directUrl); toast.success("Lien copié !"); };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose} data-testid="share-guide-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* En-tête */}
        <div className="sticky top-0 bg-white px-5 pt-5 pb-3 border-b border-slate-100 flex items-start justify-between">
          <div>
            <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2"><Check size={18} className="text-emerald-500" /> Votre carte est en ligne !</h3>
            <p className="text-xs text-slate-500 mt-1">Voici comment l'utiliser et la partager, même sur place.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1" data-testid="share-guide-close"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* 1. QR plein écran — sur place */}
          <section data-testid="guide-step-qr">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-full bg-[#E05638] text-white text-xs font-bold flex items-center justify-center">1</span>
              <h4 className="font-bold text-slate-800 text-sm flex items-center gap-1.5"><QrCode size={15} /> En personne : faites scanner votre QR code</h4>
            </div>
            <p className="text-xs text-slate-500 mb-3">La personne en face ouvre l'appareil photo de son téléphone et vise le QR : votre carte s'ouvre aussitôt. Aucune application à installer.</p>
            {!showQr ? (
              <Button onClick={() => setShowQr(true)} className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="guide-show-qr">
                <QrCode size={16} className="mr-2" /> Afficher mon QR code en grand
              </Button>
            ) : (
              <div className="bg-slate-50 rounded-xl p-4 flex flex-col items-center" data-testid="guide-qr-fullscreen">
                <img src={qrUrl(slug)} alt="QR code de ma carte" className="w-56 h-56 bg-white rounded-xl border border-slate-200 p-2" />
                <p className="text-xs text-slate-500 mt-2 text-center">{name}</p>
                <button onClick={() => setShowQr(false)} className="text-xs text-slate-400 mt-2 underline">Réduire</button>
              </div>
            )}
          </section>

          {/* 2. Ajouter à l'écran d'accueil */}
          <section className="border-t border-slate-100 pt-4" data-testid="guide-step-home">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-full bg-[#E05638] text-white text-xs font-bold flex items-center justify-center">2</span>
              <h4 className="font-bold text-slate-800 text-sm flex items-center gap-1.5"><Smartphone size={15} /> Gardez votre carte à portée de main</h4>
            </div>
            <p className="text-xs text-slate-500 mb-3">Ajoutez votre carte à l'écran d'accueil de votre téléphone : une icône, un seul geste pour la montrer ou la partager à tout moment.</p>
            <div className="grid gap-2">
              <div className="bg-slate-50 rounded-xl p-3 text-xs text-slate-600">
                <div className="font-semibold text-slate-800 flex items-center gap-1.5 mb-1"><Apple size={13} /> iPhone (Safari)</div>
                Ouvrez votre carte → bouton <b>Partager</b> (carré avec flèche) → <b>« Sur l'écran d'accueil »</b>.
              </div>
              <div className="bg-slate-50 rounded-xl p-3 text-xs text-slate-600">
                <div className="font-semibold text-slate-800 flex items-center gap-1.5 mb-1"><Smartphone size={13} /> Android (Chrome)</div>
                Ouvrez votre carte → menu <b>⋮</b> en haut à droite → <b>« Ajouter à l'écran d'accueil »</b>.
              </div>
            </div>
            <Button variant="outline" onClick={() => openExt(directUrl)} className="w-full mt-2" data-testid="guide-open-card"><Globe size={15} className="mr-1.5" /> Ouvrir ma carte</Button>
          </section>

          {/* 3. Partager le lien */}
          <section className="border-t border-slate-100 pt-4" data-testid="guide-step-share">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-full bg-[#E05638] text-white text-xs font-bold flex items-center justify-center">3</span>
              <h4 className="font-bold text-slate-800 text-sm flex items-center gap-1.5"><Share2 size={15} /> Partager à distance</h4>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => openExt(`https://wa.me/?text=${encodeURIComponent(`${text} ${shareUrl}`)}`)} className="justify-start gap-2" data-testid="guide-share-whatsapp"><MessageCircle size={16} className="text-[#25D366]" /> WhatsApp</Button>
              <Button variant="outline" onClick={() => openExt(`sms:?&body=${encodeURIComponent(`${text} ${shareUrl}`)}`)} className="justify-start gap-2" data-testid="guide-share-sms"><MessageCircle size={16} className="text-sky-500" /> SMS</Button>
              <Button variant="outline" onClick={() => openExt(`mailto:?subject=${encodeURIComponent(name)}&body=${encodeURIComponent(`${text}\n${shareUrl}`)}`)} className="justify-start gap-2" data-testid="guide-share-email"><Mail size={16} className="text-slate-500" /> E-mail</Button>
              <Button variant="outline" onClick={copy} className="justify-start gap-2" data-testid="guide-share-copy"><Link2 size={16} className="text-slate-500" /> Copier le lien</Button>
            </div>
            <Button onClick={shareNative} className="w-full mt-2 bg-[#E05638] hover:bg-[#c94a2f]" data-testid="guide-share-native"><Share2 size={15} className="mr-1.5" /> Partager via mon téléphone</Button>
          </section>

          {/* 4. vCard */}
          <section className="border-t border-slate-100 pt-4" data-testid="guide-step-vcard">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-full bg-[#E05638] text-white text-xs font-bold flex items-center justify-center">4</span>
              <h4 className="font-bold text-slate-800 text-sm flex items-center gap-1.5"><UserPlus size={15} /> Être enregistré dans leurs contacts</h4>
            </div>
            <p className="text-xs text-slate-500 mb-2">Sur votre carte publique, le bouton <b>Contact</b> enregistre vos coordonnées directement dans le téléphone de votre interlocuteur.</p>
            <Button variant="outline" onClick={() => openExt(vcardUrl(slug))} className="w-full" data-testid="guide-vcard"><UserPlus size={15} className="mr-1.5" /> Tester ma fiche contact (.vcf)</Button>
          </section>
        </div>
      </div>
    </div>
  );
}

function SeoAssistant({ cardId, onApply }) {
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    setLoading(true); setRes(null);
    try {
      const { data } = await api.post(`/cartes/${cardId}/ai/seo`);
      setRes(data.seo);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'IA SEO."); }
    finally { setLoading(false); }
  };
  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      <Button variant="outline" onClick={run} disabled={loading} className="w-full border-dashed border-[#E05638] text-[#E05638]" data-testid="seo-ai-btn">
        {loading ? <><Loader2 size={15} className="animate-spin mr-1" /> Optimisation…</> : <><Sparkles size={15} className="mr-1" /> Optimiser le référencement avec l'IA</>}
      </Button>
      {res && (
        <div className="mt-3 space-y-2 text-sm bg-orange-50/60 border border-orange-100 rounded-xl p-3" data-testid="seo-ai-result">
          <div><span className="font-semibold text-slate-700">Titre :</span> <span className="text-slate-600">{res.title}</span></div>
          <div><span className="font-semibold text-slate-700">Description :</span> <p className="text-slate-600">{res.description}</p></div>
          {Array.isArray(res.keywords) && res.keywords.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {res.keywords.map((k, i) => <span key={i} className="text-[11px] bg-white border border-slate-200 text-slate-600 rounded-full px-2 py-0.5">{k}</span>)}
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <Button size="sm" onClick={() => { onApply(res); setRes(null); toast.success("SEO appliqué !"); }} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="seo-ai-apply"><Check size={15} className="mr-1" /> Appliquer</Button>
            <Button size="sm" variant="outline" onClick={() => setRes(null)} data-testid="seo-ai-ignore">Ignorer</Button>
          </div>
        </div>
      )}
    </div>
  );
}

